import { BudgetPhases } from 'business/constants';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import {
    AMOUNT_SPECIAL_AUTHORIZATION_FIELD,
    AMOUNT_AVAILABLE_FIELD,
    BUDGET_YEAR_FIELD,
    EVOLUTION_N_N_1,
    PERCENT_REALIZED_FIELD,
    REALIZED_FIELD,
    REQUESTED_FIELD,
    SERVICE_PROPOSAL_AMOUNT,
    SIMULATED_ARBITRATED_AMOUNT,
    VOTED_FIELD,
    VOTED_TOTAL_FIELD,
    PRIMITIVE_BUDGET_AMOUNT,
    SUPPLEMENTARY_BUDGET_AMOUNT,
    ORG_CHART_FINANCE_FIELD,
    AMOUNT_AMENDING_DECISION_FIELD,
    AMOUNT_VOTED_SUP_FIELD,
    MANDATED_FIELD,
    AMOUNT_ENGAGED_UNPAID,
    REPORTED_FIELD,
    SECTION_FIELD,
    EXPENSE_INCOME_FIELD,
    INSCRIPTION_INVESTMENT_OPERATING,
    DIRECTION_CODE,
    DIRECTION_LABEL,
    ARBITRAGE_DM_AMOUNT,
    REAL_ORDER_FIELD,
    BUDGET_LINE_PROGRAM_SECTORAL_POLICY_FIELD,
    BUDGET_LINE_PROGRAM_SUB_INTERVENTION_SECTOR_FIELD,
    BUDGET_LINE_PROGRAM_INTERVENTION_SECTOR_FIELD,
    ENVELOPPE_TYPE,
} from 'routes/BudgetModule/config/constants';
import { getInfoIconTemplate } from 'helpers/iconHelper';
import {
    ColDef,
    ValueFormatterParams,
    ValueGetterParams,
} from 'ag-grid-community';
import {
    percentageGetter,
    realizedRateValueGetter,
    valueGetter,
} from 'ui/AgGridReact/getters';
import {
    budgetCurrencyFormatter,
    budgetPercentageFormatter,
    objectWithLabelFormatter,
} from 'ui/AgGridReact/formatters';
import {
    aggEvolutionGetter,
    calculateEvolution,
    calculateEvolutionInPercentage,
} from 'routes/BudgetModule/components/BudgetProposal/Resource/ExpenseIncome/gridHelpers/evolutionHelpers';
import { translateBlockValue } from 'i18n';
import { COLUMNS_FILTER_PER_INTEGRATION } from 'routes/BudgetModule/config/softwareSupport';
import { capitalize, compact, intersection } from 'lodash';
import { addSuperGestionnaireColDefs } from 'routes/BudgetModule/components/shared/helpers/addSuperGestionnaireColDefs';
import {
    CustomAggValue,
    getAnalysisDefaultGridColumnsOrdering,
} from './gridHelpers';
import { getClientBind } from 'store/APIClient';
import {
    getBudgetExcelCurrencyCellClass,
    getBudgetExcelPercentageCellClass,
} from 'routes/BudgetModule/config/excelExportHelpers';
import { isAmp } from 'routes/BudgetModule/helpers/bind';

export const SUPPLEMENTARY_BUDGET_COLUMN_FIELD = 'supplementaryBudgetColumn';

// Calculate the supplementary budget based of the data containing the voted budget and the total budget.
export const calculateSupplementaryBudget = (
    data: FlowAnyObject,
): number | null => {
    if (!data) return null;
    const primitive = data[PRIMITIVE_BUDGET_AMOUNT];
    const total = data[SUPPLEMENTARY_BUDGET_AMOUNT];
    /* supplementaryBudgetInscription.amount is the sum of the primitive budget inscriptions
     * and the supplementary budget inscriptions, so we can isolate the supplementary budget
     * by subtracting the primitive budget inscriptions from the total amount.
     */
    if (primitive != null && primitive !== 'unknown') {
        if (total != null && total !== 'unknown') {
            return total - primitive;
        }
    }
    /* if a supplementary budget (SB) inscription is added to a budget line  without any voted amount,
     * the primitive amount will be null, so we can return the total (aka new inscriptions in SB phase + the voted inscription from voted phase)
     */
    if (total != null && total !== 'unknown') {
        return total;
    }
    return null;
};

const evolutionComparator = (
    valueA: CustomAggValue,
    valueB: CustomAggValue,
) => {
    if (valueA === undefined || valueB === undefined) return 0;

    const evolutionA = calculateEvolution(
        valueA.totalAmountRequested,
        valueA.totalAmountVoted,
    );
    const evolutionB = calculateEvolution(
        valueB.totalAmountRequested,
        valueB.totalAmountVoted,
    );

    if (evolutionA === evolutionB) return 0;
    if (evolutionA === null || evolutionB === null) return 0;

    return evolutionA > evolutionB ? 1 : -1;
};

const filterColumnByIntegration =
    (softwaresIntegrations: string[]) =>
    (column: ColDef): boolean =>
        COLUMNS_FILTER_PER_INTEGRATION[column.field!]
            ? intersection(
                  COLUMNS_FILTER_PER_INTEGRATION[column.field!],
                  softwaresIntegrations,
              ).length > 0
            : true;

/**
 * Returns the column definitions for the budget pivot grid.
 *
 * Gotchas:
 * - if you add a new `budgetLine.xxx` amount field, you must also exclude it from the function that adds a duplicate N-1 budget lines
 *   to enable the N-1/N evolution calculation. See `addEvolutionColumnValues`
 */
export const getColDefs = (
    imputationDimensions: string[],
    t: TranslationT,
    budgetProposal: BudgetProposalT,
    primaryColor: string,
    onInscriptionsModalOpened: (arg1: void) => void,
    onInscriptionsModalClosed: (arg1: void) => void,
    baseFilters: QueryFilterT[],
    softwaresIntegrations: string[],
): ColDef[] => {
    const bind = getClientBind();
    const cellStylePrimaryBold = { color: primaryColor, fontWeight: 600 };
    const budgetPivotGridDimensions = imputationDimensions.filter(
        (item) => item !== EXPENSE_INCOME_FIELD,
    );

    const hasSectionFieldAsDimension =
        budgetPivotGridDimensions.includes(SECTION_FIELD);
    const baseTextColDef: ColDef = {
        sortable: true,
        filter: 'agTextColumnFilter',
        enableRowGroup: true,
        pivot: false,
        enablePivot: true,
    };
    const baseNumericColDef: ColDef = {
        sortable: true,
        filter: 'agNumberColumnFilter',
        type: 'numericColumn',
        enableRowGroup: false,
        enableValue: true,
        enablePivot: false,
        aggFunc: 'sum',
        valueFormatter: budgetCurrencyFormatter,
        filterValueGetter: valueGetter,
        cellClass: getBudgetExcelCurrencyCellClass,
    };
    const basePercentageColDef: ColDef = {
        ...baseNumericColDef,
        valueFormatter: budgetPercentageFormatter,
        filterValueGetter: percentageGetter,
        cellClass: getBudgetExcelPercentageCellClass,
    };
    const sectionColDef: ColDef = {
        ...baseTextColDef,
        field: SECTION_FIELD,
        headerName: capitalize(translateBlockValue(SECTION_FIELD, true)),
        valueGetter: (params: ValueGetterParams<ResponseDataT>) =>
            params.data?.[SECTION_FIELD] ||
            params.data?.[INSCRIPTION_INVESTMENT_OPERATING] ||
            '',
        valueFormatter: (params: ValueFormatterParams<ResponseDataT>) =>
            params.value === ''
                ? t('cellar.blanks')
                : translateBlockValue(params.value, true),
    };
    const hasSifac = softwaresIntegrations.includes('SIFAC');
    const shouldSkipSectionField = hasSifac || hasSectionFieldAsDimension;
    const colDefs: ColDef[] = compact([
        ...budgetPivotGridDimensions.map((dim) => {
            const colDef = {
                ...baseTextColDef,
                field: dim,
                headerName: capitalize(translateBlockValue(dim ?? '', true)),
            };
            if (dim === ORG_CHART_FINANCE_FIELD) {
                return {
                    ...colDef,
                    valueFormatter: objectWithLabelFormatter,
                    enablePivot: false,
                };
            }
            if (dim === SECTION_FIELD) {
                return sectionColDef;
            }
            if (dim === REAL_ORDER_FIELD) {
                return {
                    ...colDef,
                    valueFormatter: (params: ValueFormatterParams) => {
                        // We exclude the footer row otherwise it displays "Total (vide)" instead of "Total"
                        return params.value === undefined &&
                            !params.node?.footer
                            ? t('cellar.blanks')
                            : translateBlockValue(params.value);
                    },
                };
            }
            return colDef;
        }),
        // as we can filter on section field, we add it as a dimension for every integration other
        // than SIFAC which doesn't has this concept
        shouldSkipSectionField ? null : sectionColDef,
        {
            ...baseTextColDef,
            field: BUDGET_YEAR_FIELD,
            rowGroup: false,
            pivot: true,
            suppressFiltersToolPanel: false,
            suppressColumnsToolPanel: true,
            filter: true,
        },
        // for most softwares, real_order is part of the budget line ventilation fields
        // but for CIRIL it's not, so we add it here

        budgetPivotGridDimensions.includes(REAL_ORDER_FIELD)
            ? undefined
            : {
                  ...baseTextColDef,
                  field: REAL_ORDER_FIELD,
                  headerName: capitalize(
                      translateBlockValue(REAL_ORDER_FIELD ?? '', true),
                  ),
                  valueFormatter: (params: ValueFormatterParams) => {
                      // We exclude the footer row otherwise it displays "Total (vide)" instead of "Total"
                      return params.value === undefined && !params.node?.footer
                          ? t('cellar.blanks')
                          : translateBlockValue(params.value);
                  },
              },
        {
            ...baseNumericColDef,
            field: AMOUNT_AVAILABLE_FIELD,
            aggFunc: null,
        },
        {
            ...baseNumericColDef,
            field: VOTED_FIELD,
            cellStyle: cellStylePrimaryBold,
        },
        {
            ...baseNumericColDef,
            field: REPORTED_FIELD,
            aggFunc: null,
        },
        {
            ...baseNumericColDef,
            field: AMOUNT_AMENDING_DECISION_FIELD,
            aggFunc: null,
            hide: true,
        },
        {
            ...baseNumericColDef,
            field: AMOUNT_VOTED_SUP_FIELD,
            aggFunc: null,
            hide: true,
        },
        {
            ...baseNumericColDef,
            field: AMOUNT_SPECIAL_AUTHORIZATION_FIELD,
            aggFunc: null,
            hide: true,
        },
        {
            ...baseNumericColDef,
            field: VOTED_TOTAL_FIELD,
        },
        {
            ...baseNumericColDef,
            field: MANDATED_FIELD,
            aggFunc: null,
            hide: true,
        },
        {
            ...baseNumericColDef,
            field: AMOUNT_ENGAGED_UNPAID,
            headerName: t('budgetProposal.amountEngagedUnpaid'),
            aggFunc: null,
            hide: true,
        },
        {
            ...baseNumericColDef,
            field: REALIZED_FIELD,
        },
        {
            ...baseNumericColDef,
            field: PERCENT_REALIZED_FIELD,
            valueGetter: realizedRateValueGetter,
            aggFunc: 'realizedRateAggFunc',
        },
        {
            ...baseNumericColDef,
            field: REQUESTED_FIELD,
            // set aggFunc to null to hide the column when in pivot mode
            // NB 'hide' is useless for pivoted columns - see https://www.ag-grid.com/react-data-grid/pivoting/#pivot-mode--visible-columns
            aggFunc: null,
        },
    ]);

    const { phase } = budgetProposal;

    if (phase === BudgetPhases.PROPOSITION_SERVICES) {
        colDefs.push({
            ...baseNumericColDef,
            field: SERVICE_PROPOSAL_AMOUNT,
            cellRenderer: 'amountWithInscriptionsButtonCellRenderer',
            cellRendererParams: {
                onInscriptionsModalOpened,
                onInscriptionsModalClosed,
                baseFilters,
                budgetProposal,
            },
            cellStyle: {},
        });
    } else {
        colDefs.push({
            ...baseNumericColDef,
            // we want this column to be displayed even during the arbitration
            field: SERVICE_PROPOSAL_AMOUNT,
            cellStyle: phase === BudgetPhases.VOTE ? {} : cellStylePrimaryBold,
        });
    }

    colDefs.push({
        ...basePercentageColDef,
        field: EVOLUTION_N_N_1,
        headerComponentParams: { template: getInfoIconTemplate(primaryColor) },
        headerTooltip: t('budgetProposal.evolutionHeaderTooltip'),
        aggFunc: 'evolutionAggFunc',
        // We need to be careful here, if the following functions valueFormatter or comparator
        // are anonymous or if they depend on other parameters, they will be new function at every render
        // and ag grid will detect new changes in the grid and it will trigger recurrent PATCH request
        // that will break some features.
        valueFormatter: (params: ValueFormatterParams) =>
            params.value
                ? calculateEvolutionInPercentage(
                      params.value.totalAmountRequested,
                      params.value.totalAmountVoted,
                      t,
                  ) || ''
                : '',
        comparator: evolutionComparator,
        filterValueGetter: aggEvolutionGetter,
    });

    if (phase === BudgetPhases.ARBITRAGE) {
        colDefs.push({
            ...baseNumericColDef,
            field: SIMULATED_ARBITRATED_AMOUNT,
            cellRenderer: 'amountWithInscriptionsButtonCellRenderer',
            cellRendererParams: {
                onInscriptionsModalOpened,
                onInscriptionsModalClosed,
                baseFilters,
                budgetProposal,
            },
        });
    }
    if (phase === BudgetPhases.VOTE) {
        colDefs.push({
            ...baseNumericColDef,
            field: PRIMITIVE_BUDGET_AMOUNT,
            cellRenderer: 'amountWithInscriptionsButtonCellRenderer',
            cellRendererParams: {
                onInscriptionsModalOpened,
                onInscriptionsModalClosed,
                baseFilters,
                budgetProposal,
            },
        });
    }
    // (Shortcut ID : 30915) : add amounts for ampmetropole
    if (phase === BudgetPhases.SUPPLEMENTARY_BUDGET || isAmp(bind)) {
        const defaultHidden =
            phase !== BudgetPhases.SUPPLEMENTARY_BUDGET && isAmp(bind);

        if (phase !== BudgetPhases.VOTE) {
            colDefs.push({
                ...baseNumericColDef,
                field: PRIMITIVE_BUDGET_AMOUNT,
                headerName: t('budgetProposal.primitiveBudget'),
                hide: defaultHidden,
                initialAggFunc: undefined,
            });
        }

        colDefs.push({
            ...baseNumericColDef,
            field: SUPPLEMENTARY_BUDGET_COLUMN_FIELD,
            headerName: t('budgetProposal.supplementaryBudget'),
            cellStyle: cellStylePrimaryBold,
            valueGetter: ({ data }) => calculateSupplementaryBudget(data),
            hide: defaultHidden,
            initialAggFunc: undefined,
        });
        colDefs.push({
            ...baseNumericColDef,
            field: SUPPLEMENTARY_BUDGET_AMOUNT,
            headerName: t('budgetProposal.totalBudget'),
            cellRenderer: 'amountWithInscriptionsButtonCellRenderer',
            cellRendererParams: {
                onInscriptionsModalOpened,
                onInscriptionsModalClosed,
                baseFilters,
                budgetProposal,
            },
            hide: defaultHidden,
            initialAggFunc: undefined,
        });
    }
    if (phase === BudgetPhases.ARBITRAGE_DM) {
        colDefs.push({
            ...baseNumericColDef,
            field: ARBITRAGE_DM_AMOUNT,
            headerName: t('budgetProposal.arbitrageDmAmount'),
            cellRenderer: 'amountWithInscriptionsButtonCellRenderer',
            cellRendererParams: {
                onInscriptionsModalOpened,
                onInscriptionsModalClosed,
                baseFilters,
                budgetProposal,
            },
            initialAggFunc: undefined,
        });
        if (isAmp(bind)) {
            colDefs.push({
                ...baseNumericColDef,
                field: 'totalAmount',
                headerName: t('budgetProposal.totalBudgetDM'),
                cellStyle: cellStylePrimaryBold,
                initialAggFunc: undefined,
            });
        }
    }

    if (softwaresIntegrations.includes('CIRIL_FINANCES')) {
        addSuperGestionnaireColDefs(colDefs);
    }

    if (softwaresIntegrations.includes('SEDIT_FINANCES')) {
        colDefs.push({
            ...baseTextColDef,
            field: DIRECTION_CODE,
            headerName: capitalize(translateBlockValue(DIRECTION_CODE, true)),
        });

        colDefs.push({
            ...baseTextColDef,
            field: DIRECTION_LABEL,
            headerName: capitalize(translateBlockValue(DIRECTION_LABEL, true)),
        });
    }

    if (softwaresIntegrations.includes('GRAND_ANGLE')) {
        colDefs.push({
            ...baseTextColDef,
            field: BUDGET_LINE_PROGRAM_SECTORAL_POLICY_FIELD,
            headerName: capitalize(
                translateBlockValue(
                    BUDGET_LINE_PROGRAM_SECTORAL_POLICY_FIELD,
                    true,
                ),
            ),
        });
        colDefs.push({
            ...baseTextColDef,
            field: BUDGET_LINE_PROGRAM_SUB_INTERVENTION_SECTOR_FIELD,
            headerName: capitalize(
                translateBlockValue(
                    BUDGET_LINE_PROGRAM_SUB_INTERVENTION_SECTOR_FIELD,
                    true,
                ),
            ),
        });
        colDefs.push({
            ...baseTextColDef,
            field: BUDGET_LINE_PROGRAM_INTERVENTION_SECTOR_FIELD,
            headerName: capitalize(
                translateBlockValue(
                    BUDGET_LINE_PROGRAM_INTERVENTION_SECTOR_FIELD,
                    true,
                ),
            ),
        });
        colDefs.push({
            ...baseTextColDef,
            field: ENVELOPPE_TYPE,
            headerName: capitalize(translateBlockValue(ENVELOPPE_TYPE, true)),
        });
    }

    const fieldsList = getAnalysisDefaultGridColumnsOrdering();
    return colDefs
        .filter(filterColumnByIntegration(softwaresIntegrations))
        .map((col) => ({
            ...col,
            field: col.field,
            headerName:
                col.headerName || translateBlockValue(col.field ?? '', true),
        }))
        .sort((a, b) => {
            const indexA = fieldsList.indexOf(a.field);
            const indexB = fieldsList.indexOf(b.field);
            return (
                (indexA === -1 ? Infinity : indexA) -
                (indexB === -1 ? Infinity : indexB)
            );
        });
};

/**
 * Some columns do not make sense if the given exercice is in the future. E.g we are in 2025
 * and we want to display the "realized" column for 2026: the budget won't be voted before 2026
 * so it cannot have any realized amount.
 */
const shouldHideIfExerciceInTheFuture = (exercice: number) =>
    exercice > new Date().getFullYear();

/** Customize configuration for some fields according the pivoted year under which the field is.
 *
 * For instance it allows us to display columns depending upon the pivoted year (ex: under exercice - 3 we only display REQUEST_FIELD).
 */
export const getColDefsForColumnsPivotedByYear = (
    exercice: number,
    phase: BudgetPhases,
    bind?: string,
): Record<number, Record<string, Pick<ColDef, 'hide'>>> => ({
    [exercice - 3]: {
        [REQUESTED_FIELD]: {
            hide: false,
        },
        [VOTED_FIELD]: {
            hide: true,
        },
        [VOTED_TOTAL_FIELD]: {
            hide: true,
        },
        [PERCENT_REALIZED_FIELD]: {
            hide: true,
        },
        [AMOUNT_AVAILABLE_FIELD]: {
            hide: true,
        },
        [EVOLUTION_N_N_1]: {
            hide: true,
        },
        [SUPPLEMENTARY_BUDGET_COLUMN_FIELD]: {
            hide: true,
        },
        [AMOUNT_AMENDING_DECISION_FIELD]: {
            hide: true,
        },
        [AMOUNT_SPECIAL_AUTHORIZATION_FIELD]: {
            hide: true,
        },
        [AMOUNT_VOTED_SUP_FIELD]: {
            hide: true,
        },
        [REPORTED_FIELD]: {
            hide: true,
        },
        [SERVICE_PROPOSAL_AMOUNT]: {
            hide: true,
        },
        [SIMULATED_ARBITRATED_AMOUNT]: {
            hide: true,
        },
        [PRIMITIVE_BUDGET_AMOUNT]: {
            hide: true,
        },
        [SUPPLEMENTARY_BUDGET_AMOUNT]: {
            hide: true,
        },
        [ARBITRAGE_DM_AMOUNT]: {
            hide: true,
        },
    },
    [exercice - 2]: {
        [REQUESTED_FIELD]: {
            hide: false,
        },
        [VOTED_FIELD]: {
            hide: true,
        },
        [VOTED_TOTAL_FIELD]: {
            hide: true,
        },
        [PERCENT_REALIZED_FIELD]: {
            hide: true,
        },
        [SERVICE_PROPOSAL_AMOUNT]: {
            hide: true,
        },
        [SIMULATED_ARBITRATED_AMOUNT]: {
            hide: true,
        },
        [PRIMITIVE_BUDGET_AMOUNT]: {
            hide: true,
        },
        [ARBITRAGE_DM_AMOUNT]: {
            hide: true,
        },
        [AMOUNT_AVAILABLE_FIELD]: {
            hide: true,
        },
        [EVOLUTION_N_N_1]: {
            hide: true,
        },
        [SUPPLEMENTARY_BUDGET_COLUMN_FIELD]: {
            hide: true,
        },
        [SUPPLEMENTARY_BUDGET_AMOUNT]: {
            hide: true,
        },
        [AMOUNT_AMENDING_DECISION_FIELD]: {
            hide: true,
        },
        [AMOUNT_SPECIAL_AUTHORIZATION_FIELD]: {
            hide: true,
        },
        [AMOUNT_VOTED_SUP_FIELD]: {
            hide: true,
        },
        [REPORTED_FIELD]: {
            hide: true,
        },
    },
    [exercice - 1]: {
        [REQUESTED_FIELD]: {
            hide: false,
        },
        [SERVICE_PROPOSAL_AMOUNT]: {
            hide: true,
        },
        [SIMULATED_ARBITRATED_AMOUNT]: {
            hide: true,
        },
        [PRIMITIVE_BUDGET_AMOUNT]: {
            hide: true,
        },
        [ARBITRAGE_DM_AMOUNT]: {
            hide: true,
        },
        [AMOUNT_AVAILABLE_FIELD]: {
            hide: false,
        },
        [EVOLUTION_N_N_1]: {
            hide: true,
        },
        [SUPPLEMENTARY_BUDGET_COLUMN_FIELD]: {
            hide: true,
        },
        [SUPPLEMENTARY_BUDGET_AMOUNT]: {
            hide: true,
        },
        [REPORTED_FIELD]: {
            hide: true,
        },
    },
    [exercice]: {
        [VOTED_FIELD]: {
            hide: true,
        },
        [VOTED_TOTAL_FIELD]: {
            hide: true,
        },
        [AMOUNT_AVAILABLE_FIELD]: {
            hide: false,
        },
        [AMOUNT_ENGAGED_UNPAID]: {
            hide: shouldHideIfExerciceInTheFuture(exercice),
        },
        [MANDATED_FIELD]: {
            hide: shouldHideIfExerciceInTheFuture(exercice),
        },
        [REALIZED_FIELD]: {
            hide: shouldHideIfExerciceInTheFuture(exercice),
        },
        [PERCENT_REALIZED_FIELD]: {
            hide: shouldHideIfExerciceInTheFuture(exercice),
        },
        [EVOLUTION_N_N_1]: {
            hide: false,
        },
        [REQUESTED_FIELD]: {
            hide: false,
        },
        [AMOUNT_AMENDING_DECISION_FIELD]: {
            hide:
                phase !== BudgetPhases.SUPPLEMENTARY_BUDGET &&
                !isAmp(bind ?? ''),
        },
        [AMOUNT_SPECIAL_AUTHORIZATION_FIELD]: {
            hide: false,
        },
        [AMOUNT_VOTED_SUP_FIELD]: {
            hide: true,
        },
        [DIRECTION_CODE]: {
            hide: true,
        },
        [DIRECTION_LABEL]: {
            hide: true,
        },
    },
});
